import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, HostListener, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslocoModule } from '@jsverse/transloco';
import { provideIcons } from '@ng-icons/core';
import {
  matArrowRight,
  matKeyboardArrowDown,
} from '@ng-icons/material-icons/baseline';
import { LanguageService } from '../../../../language.service';
import { UserService } from '../../../../platform/services/user-service/userservice.service';
import { AppDataState } from '../../../state-stores/app-data-state.state';
import { initFlowbite } from 'flowbite';
import { MobilenavComponent } from "../../mobilenav/mobilenav.component";
import { ProfileComponent } from "../../profile/profile.component";
import { ContactusComponent } from "../../contactus/contactus.component";
import { AppScrollableState } from '../../../state-stores/app-scrollable.state';
import PermissionsUtil from '../../../util/permissions-util';
import { matSettingsOutline } from '@ng-icons/material-icons/outline';
import { ConfigurationModalComponent } from '../../../../flowpoint-apps/station-app/components/configuration-modal/configuration-modal.component';
import { ModalState } from '../../../state-stores/modal-state.state';
import { environment } from '../../../../../environments/environment';
import { PermissionSetType } from '../../../../platform/interfaces/permission-set';
import { NotificationLogService } from '../../../../flowpoint-apps/station-app/services/notification-log-service/notification-log.service';
import { NotificationLog } from '../../../../flowpoint-apps/station-app/interfaces/notification-log';
import { SortDirection, SortParams } from '../../../interfaces/sort-params';
import { NotificationPanelComponent } from '../../notification-panel/notification-panel.component';
import { Subscription, interval } from 'rxjs';
import { NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-platformheader',
  imports: [CommonModule, TranslocoModule, MobilenavComponent, ProfileComponent, ContactusComponent, ConfigurationModalComponent, NotificationPanelComponent],
  templateUrl: './platformheader.component.html',
  styleUrl: './platformheader.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  viewProviders: [provideIcons({ matSettingsOutline })],
})
export class PlatformheaderComponent implements OnInit, OnDestroy {
  matArrowRight: any;
  isMobileMenuOpen = false;
  isUserMenuOpen = false; 
  isHelpMenuOpen = false;
  isLanguageMenuOpen = false;
  isMobileNavLoading = true;

  activeLanguage: string = localStorage.getItem('selectedLanguage') || 'en';

  readonly availableLanguages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'es', label: 'Español' },
    { code: 'ja', label: '日本語' },
  ];

  /**
   * Whether to show the profile modal
   */
  showProfileModalToggle = false;

  /**
   * Whether the profile is loading
   */
  profileLoading = false;

  /**
   * Whether to show the contact us modal
   */
  showContactUsModal = false;

  /**
   * Whether to show the configuration modal
   */
  showConfigModal = false;

  /**
   * Whether the notification panel is open
   */
  isNotificationPanelOpen = false;

  /**
   * Notification logs displayed in the panel
   */
  notificationLogs: NotificationLog[] = [];

  /**
   * Whether notifications are loading
   */
  notificationsLoading = false;

  /**
   * Whether a notification load error occurred
   */
  notificationsError = false;

  readNotificationIds = new Set<number>();

  private notificationPollingSubscription: Subscription | null = null;
  private routerEventsSubscription: Subscription | null = null;
  private readonly NOTIFICATION_POLL_INTERVAL_MS = 2 * 60 * 1000;

  /**
   * Configuration ID for editing (undefined for create mode)
   */
  configurationId?: number;

  /**
   * Whether the current user is a super user
   */
  isSuperUser = false;

  /**
   * Whether the current user can process customer applications (gates the notification bell)
   */
  canProcessConsumerApplications = false;

  userPermissions: string[] = [];
  isOperatorLogoLoadFailed = false;
  private currentOperatorLogoUrl = '';

  constructor(
    private readonly userSerivce: UserService,
    protected readonly router: Router,
    protected readonly authService: MsalService,
    private readonly languageService: LanguageService,
    private readonly appDataState: AppDataState,
    private eRef: ElementRef,
    protected readonly appScrollableState: AppScrollableState,
    protected readonly modalState: ModalState,
    private readonly notificationLogService: NotificationLogService
  ) {}

  async ngOnInit(): Promise<void> {
    // Simulate loading, replace with real loading logic if needed
    setTimeout(() => {
      this.isMobileNavLoading = false;
      this.appScrollableState.setScrollable(!this.isMobileMenuOpen);
    }, 1000);

    initFlowbite();
    const appName = this.router.url.split('/')[1];

    this.loadReadIds();

    const userPermissions = await PermissionsUtil.getPermissionSets(this.userSerivce, false);
    
    // Check if user is a super user
    await this.getRequiredUserPermissions();

    // Start polling if already on station app, and watch for route changes
    if (this.isStationApp() && this.canProcessConsumerApplications) {
      await this.loadNotifications();
      this.startNotificationPolling();
    }

    this.routerEventsSubscription = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(async (e) => {
        const isStation = (e as NavigationEnd).urlAfterRedirects.includes('/stationapp');
        if (isStation && this.canProcessConsumerApplications && !this.notificationPollingSubscription) {
          await this.loadNotifications();
          this.startNotificationPolling();
        } else if (!isStation || !this.canProcessConsumerApplications) {
          this.notificationPollingSubscription?.unsubscribe();
          this.notificationPollingSubscription = null;
        }
      });
  }

  ngOnDestroy(): void {
    this.notificationPollingSubscription?.unsubscribe();
    this.routerEventsSubscription?.unsubscribe();
  }

  toggleMobileMenu() {
    // <-- Add this method
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.appScrollableState.setScrollable(!this.isMobileMenuOpen);
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    this.appScrollableState.setScrollable(!this.isMobileMenuOpen);

  }

  showProfileModal() {
    // Close any other open modals first
    this.showContactUsModal = false;
    
    // Then open the profile modal
    this.showProfileModalToggle = true;
    this.isUserMenuOpen = false; // Close the user menu when opening profile modal
    this.isMobileMenuOpen = false; // Close the mobile menu when opening profile modal
  }

  /**
   * Function to close the profile modal
   */
  closeProfileModal = (): void => {
    this.showProfileModalToggle = false;
    this.modalState.setModalOpen(false);
  };

  /**
   * Function called after successful profile save
   */
  handleProfileSave = (): void => {
    console.log('Profile saved successfully!');
    // Optionally close modal automatically after save
    // this.showProfileModal = false;
  };

  navigateTo(path: string) {
    this.router.navigate([path]);
  }

  logout() {
    sessionStorage.clear();
    localStorage.clear();
    this.authService.logoutRedirect();
  }

  switchLanguage(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const lang = selectElement.value;
    this.languageService.switchLanguage(lang);
  }

  setLanguage(lang: string) {
    this.activeLanguage = lang;
    this.languageService.switchLanguage(lang);
    this.isLanguageMenuOpen = false;
  }

  contactUs() {
    // Close any other open modals first
    this.showProfileModalToggle = false;
    
    // Then open the contact us modal
    this.showContactUsModal = true;
    this.isHelpMenuOpen = false; // Close the help menu when opening contact us modal
    this.isMobileMenuOpen = false; // Close the mobile menu when opening contact us modal
  }

  /**
   * Function to close the contact us modal
   */
  closeContactUsModal = (): void => {
    this.showContactUsModal = false;
  };

  /**
   * Function to show the configuration modal
   */
  showConfiguration() {
    // Close any other open modals first
    this.showProfileModalToggle = false;
    this.showContactUsModal = false;
    
    // Then open the configuration modal
    this.showConfigModal = true;
    this.configurationId = undefined; // Create new configuration
    this.isUserMenuOpen = false;
    this.isHelpMenuOpen = false;
    this.isMobileMenuOpen = false;
  }

  /**
   * Function to close the configuration modal
   */
  closeConfigModal = (): void => {
    this.showConfigModal = false;
    this.configurationId = undefined;
  };

  /**
   * Function called after successful configuration save
   */
  handleConfigSave = (configuration: any): void => {
  };

  async toggleNotificationPanel(): Promise<void> {
    const wasOpen = this.isNotificationPanelOpen;
    this.closeOtherDropdowns('notification');
    this.isNotificationPanelOpen = !wasOpen;
    if (wasOpen) {
      // Mark all as read when closing the panel
      this.markAllAsRead();
    }
  }

  private startNotificationPolling(): void {
    this.notificationPollingSubscription = interval(this.NOTIFICATION_POLL_INTERVAL_MS)
      .subscribe(() => this.loadNotifications());
  }

  navigateToNotificationSource(source: string | undefined, id: number | undefined): void {
    if (id != null) this.markAsRead(id);
    if (!source) return;
    this.isNotificationPanelOpen = false;
    this.isMobileMenuOpen = false;
    this.appScrollableState.setScrollable(true);
    this.router.navigate(['/stationapp', source]);
  }

  private async loadNotifications(): Promise<void> {
    this.notificationsLoading = true;
    this.notificationsError = false;
    const sortParams: SortParams = { sortBy: 'createdAtUTC', sortDirection: SortDirection.DESC };
    try {
      const result = await this.notificationLogService.get(null, [], sortParams, null, { first: 30 });
      this.notificationLogs = result.data ?? [];
      const currentIds = this.notificationLogs.map((n: NotificationLog) => n.id).filter((id): id is number => id != null);
      this.pruneStaleReadIds(currentIds);
    } catch {
      this.notificationsError = true;
    } finally {
      this.notificationsLoading = false;
    }
  }

  get unreadCount(): number {
    return this.notificationLogs.filter(n => n.id != null && !this.readNotificationIds.has(n.id!)).length;
  }

  private markAsRead(id: number): void {
    this.readNotificationIds.add(id);
    this.saveReadIds();
  }

  private markAllAsRead(): void {
    this.notificationLogs.forEach(n => { if (n.id != null) this.readNotificationIds.add(n.id!); });
    this.saveReadIds();
  }

  private pruneStaleReadIds(currentIds: number[]): void {
    const current = new Set(currentIds);
    for (const id of Array.from(this.readNotificationIds)) {
      if (!current.has(id)) this.readNotificationIds.delete(id);
    }
    this.saveReadIds();
  }

  private get readIdsStorageKey(): string {
    return `notification_read_ids_${this.appDataState.getAppInstanceId() ?? 'default'}`;
  }

  private loadReadIds(): void {
    try {
      const raw = localStorage.getItem(this.readIdsStorageKey);
      const ids: number[] = raw ? JSON.parse(raw) : [];
      this.readNotificationIds = new Set(ids);
    } catch {
      this.readNotificationIds = new Set();
    }
  }

  private saveReadIds(): void {
    try {
      localStorage.setItem(this.readIdsStorageKey, JSON.stringify(Array.from(this.readNotificationIds)));
    } catch { /* storage quota exceeded — ignore */ }
  }

  closeOtherDropdowns(except?: string) {
    if (except !== 'user') {
      this.isUserMenuOpen = false;
    }
    if (except !== 'help') {
      this.isHelpMenuOpen = false;
    }
    if (except !== 'language') {
      this.isLanguageMenuOpen = false;
    }
    if (except !== 'notification') {
      if (this.isNotificationPanelOpen) {
        this.markAllAsRead();
      }
      this.isNotificationPanelOpen = false;
    }
  }

  onHeaderClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    // Keep menus open when clicking inside menu triggers/panels.
    if (target.closest('[data-menu-trigger]') || target.closest('[data-menu-panel]')) {
      return;
    }

    this.closeOtherDropdowns();
  }

  getUserName(): string {
    let activeAccount = this.authService.instance.getActiveAccount();
    return activeAccount ? activeAccount.name! : '';
  }

  getAppName(): string {
    const url = this.router.url;
    
    // Return empty string for home/platform pages
    if (url === '/platform/home' || url === '/platform' || url === '/') {
      return '';
    }
    
    // Map URL segments to app names
    
    if (url.includes('/stationstatus')) {
      return 'Station Monitoring';
    }
    if (url.includes('/station')) {
      return 'Water+';
    }
    if (url.includes('/trashpass')) {
      return 'TrashPass';
    }
    if (url.includes('/watermain')) {
      return 'WaterMain';
    }
    
    // Return empty for unmapped routes
    return '';
  }

  getOperatorName(): string {
    // Get operator name from appDataState
    // TODO: This should fetch the actual operator name from the operator table
    return this.appDataState.getOperatorName() ?? 'Operator';
  }

  getOperatorLogoUrl(): string {
    const operatorName = this.getOperatorName();
    const operatorSlug = operatorName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const logoUrl = operatorSlug
      ? `https://watermainstaticdata.blob.core.windows.net/operator-logos/${operatorSlug}.png`
      : '';

    // If the target URL changed, allow a fresh load attempt for the new logo.
    if (logoUrl !== this.currentOperatorLogoUrl) {
      this.currentOperatorLogoUrl = logoUrl;
      this.isOperatorLogoLoadFailed = false;
    }

    return logoUrl;
  }

  onOperatorLogoError(): void {
    this.isOperatorLogoLoadFailed = true;
  }
  
  getOperatorLogo(): string | null {
    return this.appDataState.getOperatorLogo();
  }

  getOperatorLogoDataUri(): string {
    const b64 = this.appDataState.getOperatorLogo();
    if (!b64) return '';
    // Detect MIME type from base64 magic bytes
    let mime = 'image/png';
    if (b64.startsWith('/9j/')) mime = 'image/jpeg';
    else if (b64.startsWith('R0lGOD')) mime = 'image/gif';
    else if (b64.startsWith('UklGR')) mime = 'image/webp';
    return `data:${mime};base64,${b64}`;
  }

  isStationApp(): boolean {
    const url = this.router.url;
    return url.includes('/stationapp');
  }

  getFullPath(segment: string, url: string): string {
    const path = url.includes(segment) ? url.split(segment)[0] + segment : url;
    return path.split('/').filter(Boolean).length === 1 ? `${path}/home` : path;
  }

  getPathWithoutParams(path: string): string {
    return path.split('?')[0];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (
      this.isUserMenuOpen &&
      !this.eRef.nativeElement.contains(event.target)
    ) {
      this.isUserMenuOpen = false;
    }
    if (this.isHelpMenuOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isHelpMenuOpen = false;
    }
    if (this.isLanguageMenuOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isLanguageMenuOpen = false;
    }
    if (this.isNotificationPanelOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isNotificationPanelOpen = false;
    }
  }

  /**
   * Check if the current user has super user permissions
   */
  private async getRequiredUserPermissions(): Promise<void> {
    try {
      const userPermissions = await PermissionsUtil.getPermissionSets(this.userSerivce, false);
      const appName = this.appDataState.getAppName() ?? 'Platform';
      const userAppPermissions = userPermissions.get(`${appName}-${this.appDataState.getOperatorId()}-${this.appDataState.getPermissionSetId()}`)?.permissions || [];
      
      // Check if user has SuperUser permission
      this.isSuperUser = PermissionsUtil.checkPermissions(['SuperUser'], userAppPermissions);
      this.canProcessConsumerApplications = PermissionsUtil.checkPermissions(['ConsumerApplication.Process'], userAppPermissions);
    } catch (error) {
      console.error('Error checking super user permissions:', error);
      this.isSuperUser = false;
      this.canProcessConsumerApplications = false;
    }
  }

  public checkStationAppConfigurationPermissions = (): boolean =>{
        return this.appDataState.isStationAppConfigPermissionPresent();
  }

  /**
   * Checks if the current user has Admin permission set type
   * 
   * Defaults to false (non-admin) if permission type is not set for security
   * 
   * @returns true if user is Admin, false otherwise
   */
  isAdmin(): boolean {
    const permissionSetType = this.appDataState.getPermissionSetType();
    return permissionSetType === PermissionSetType.Admin;
  }

  /**
   * Downloads the appropriate manual PDF based on user's permission set type
   * - ADMIN users: Downloads Operator/Vendor Manual
   * - USER users: Downloads Customer Manual
   * 
   * Uses environment configuration for URL management
   * Includes error handling with fallback to opening in new tab
   */
  async downloadManual(): Promise<void> {
    // Close menus after clicking for better UX
    this.isHelpMenuOpen = false;
    this.isMobileMenuOpen = false;

    // Determine which manual to download based on user role
    const isAdminUser = this.isAdmin();
    const pdfUrl = isAdminUser 
      ? environment.manuals.operatorManualUrl 
      : environment.manuals.customerManualUrl;
    
    const fileName = isAdminUser
      ? 'Fusion_WaterPlus_VendorOperator_Manual.pdf'
      : 'Fusion_WaterPlus_Customer_Manual.pdf';

    try {
      // Fetch the PDF from Azure Blob Storage
      const response = await fetch(pdfUrl);
      
      // Check if the request was successful
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Convert the response to a blob (binary data)
      const blob = await response.blob();
      
      // Create a temporary URL that points to the blob in memory
      const url = window.URL.createObjectURL(blob);
      
      // Create an invisible anchor element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up: revoke the blob URL to free memory
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      // Log error for debugging
      console.error('Error downloading manual:', error);
      
      // Fallback: Open PDF in new tab if fetch fails (e.g., CORS issues)
      // User can then manually save from their browser
      window.open(pdfUrl, '_blank');
    }
  }
}
